/* @jsxImportSource react */
'use client';

import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { Collapsible as CollapsibleBase } from '@base-ui/react/collapsible';

import { cn } from '../../utils/cn';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

// =============================================================================
// Variants
// =============================================================================

export const ATOMS_COLLAPSIBLE_VARIANTS = {} as const;

export const ATOMS_COLLAPSIBLE_DEFAULT_VARIANTS = {} as const;

export interface AtomsCollapsibleVariantsProps {}

export function collapsibleVariants(_props: AtomsCollapsibleVariantsProps = {}) {
  return cn();
}

// =============================================================================
// Collapsible Root
// =============================================================================

type BaseRootProps = StripUndesiredProps<ComponentPropsWithoutRef<typeof CollapsibleBase.Root>>;

export interface CollapsibleRootProps extends BaseRootProps {
  className?: string;
}

function CollapsibleRoot({ className, ...props }: CollapsibleRootProps) {
  return <CollapsibleBase.Root className={className} {...props} />;
}

CollapsibleRoot.displayName = 'Collapsible.Root';

// =============================================================================
// Collapsible Trigger
// =============================================================================

type BaseTriggerProps = StripUndesiredProps<
  ComponentPropsWithoutRef<typeof CollapsibleBase.Trigger>
>;

export interface CollapsibleTriggerProps extends BaseTriggerProps {
  className?: string;
}

const CollapsibleTrigger = forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(
  ({ className, ...props }, ref) => {
    return (
      <CollapsibleBase.Trigger ref={ref} className={cn('cursor-pointer', className)} {...props} />
    );
  },
);

CollapsibleTrigger.displayName = 'Collapsible.Trigger';

// =============================================================================
// Collapsible Panel
// =============================================================================

type BasePanelProps = StripUndesiredProps<ComponentPropsWithoutRef<typeof CollapsibleBase.Panel>>;

export interface CollapsiblePanelProps extends BasePanelProps {
  className?: string;
}

const CollapsiblePanel = forwardRef<HTMLDivElement, CollapsiblePanelProps>(
  ({ className, ...props }, ref) => {
    return <CollapsibleBase.Panel ref={ref} className={className} {...props} />;
  },
);

CollapsiblePanel.displayName = 'Collapsible.Panel';

// =============================================================================
// Compound Component Export
// =============================================================================

/**
 * Compound component for show/hide disclosure regions. Use it for expandable
 * sections, FAQ entries, or any content that toggles open and closed.
 *
 * Compose it from its sub-components: `Collapsible.Root` (also the default
 * export) wraps the region and owns open/close state, `Collapsible.Trigger` is
 * the button that toggles it, and `Collapsible.Panel` holds the collapsible
 * content. Open state can be controlled (`open` + `onOpenChange`) or
 * uncontrolled (`defaultOpen`) via the underlying Base UI props.
 *
 * @example
 * ```tsx
 * <Collapsible.Root defaultOpen={false}>
 *   <Collapsible.Trigger>Show details</Collapsible.Trigger>
 *   <Collapsible.Panel>
 *     <p>Hidden content revealed when expanded.</p>
 *   </Collapsible.Panel>
 * </Collapsible.Root>
 * ```
 */
export const Collapsible = Object.assign(CollapsibleRoot, {
  Root: CollapsibleRoot,
  Trigger: CollapsibleTrigger,
  Panel: CollapsiblePanel,
});

// =============================================================================
// Type Exports
// =============================================================================

export type CollapsibleProps = CollapsibleRootProps;
